import { useState } from "react";
import { invoke } from "../lib/bridge";
import { Alert } from "./ui/Alert";
import { Footer } from "./Footer";
import type { FamilyInfo } from "../lib/onboardingTypes";

interface CreateFamilyProps {
  onCreated: (info: FamilyInfo) => void;
  onJoinWithCode: () => void;
}

export function CreateFamily({ onCreated, onJoinWithCode }: CreateFamilyProps) {
  const [familyName, setFamilyName] = useState("");
  const [memberName, setMemberName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!familyName.trim() || !memberName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const info = await invoke<FamilyInfo>("init_family", {
        familyName: familyName.trim(),
        memberName: memberName.trim(),
      });
      onCreated(info);
    } catch (e) {
      console.error("Failed to create family:", e);
      setError("Could not create your family. Please try again.");
      setCreating(false);
    }
  };

  return (
    <main className="container">
      <h1 className="family-name">Family App</h1>
      <p style={{ color: "var(--color-muted)", marginBottom: "28px",  marginTop: "12px", lineHeight: 1.5 }}>
        Stop being the one who remembers everything. Give your family a single private space for the list, the plan, and the "wait, where did that go?" Don't use five apps, when Family App can do it all.
      </p>
      <div style={{ marginBottom: "24px" }}>
        <label className="field-label" htmlFor="create-family-name">What should we call your family?</label>
        <input
          id="create-family-name"
          value={familyName}
          onChange={(e) => setFamilyName(e.target.value)}
          placeholder="The Smiths"
          className="field-input"
        />
      </div>
      <div style={{ marginBottom: "24px" }}>
        <label className="field-label" htmlFor="create-member-name">What's your name?</label>
        <input
          id="create-member-name"
          value={memberName}
          onChange={(e) => setMemberName(e.target.value)}
          placeholder="Mom"
          className="field-input"
        />
      </div>
      {error && <Alert>{error}</Alert>}
      <button
        onClick={handleCreate}
        disabled={creating || !familyName.trim() || !memberName.trim()}
        className="btn-primary btn-full"
      >
        {creating ? "Creating..." : "Create Family"}
      </button>
      <div style={{ textAlign: "center", margin: "24px 0", color: "var(--color-muted)" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "12px" }}>
          <span style={{ width: "60px", height: "1px", background: "var(--color-border)", display: "inline-block" }} />
          or
          <span style={{ width: "60px", height: "1px", background: "var(--color-border)", display: "inline-block" }} />
        </span>
      </div>
      <button
        onClick={onJoinWithCode}
        className="btn-secondary btn-full"
      >
        Join Family with invite code
      </button>
      <div style={{ marginTop: "24px", paddingTop: "32px" }}>
        <p style={{ color: "var(--color-muted)", fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "20px" }}>
          Why Family App?
        </p>
        <div>
        <p style={{ fontWeight: 600, marginBottom: "8px" }}>Are you the household CEO?</p>
        <p style={{ color: "var(--color-muted)", fontSize: "14px", lineHeight: 1.5 }}>
          Do you carry the bulk of the mental load for the entire family? That's exactly what
          Family App solves, by sharing that mental load with every member of the family.
        </p>
      </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {[
            {
              icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M22,17 L22,19 L8,19 L8,17 L22,17 Z M22,11 L22,13 L8,13 L8,11 L22,11 Z M22,5 L22,7 L8,7 L8,5 L22,5 Z M4,20 C2.8954305,20 2,19.1045695 2,18 C2,16.8954305 2.8954305,16 4,16 C5.1045695,16 6,16.8954305 6,18 C6,19.1045695 5.1045695,20 4,20 Z M4,14 C2.8954305,14 2,13.1045695 2,12 C2,10.8954305 2.8954305,10 4,10 C5.1045695,10 6,10.8954305 6,12 C6,13.1045695 5.1045695,14 4,14 Z M4,8 C2.8954305,8 2,7.1045695 2,6 C2,4.8954305 2.8954305,4 4,4 C5.1045695,4 6,4.8954305 6,6 C6,7.1045695 5.1045695,8 4,8 Z" />
                </svg>
              ),
              title: "Everyone sees the same list",
              body: "Add tortillas from the store. Your partner sees it before you're home. No more texting a photo of the fridge.",
            },
            {
              icon: (
                <svg width="20" height="20" viewBox="0 0 256 256" fill="none" stroke="currentColor" strokeWidth="16" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                  <path d="m 127.99999,239.96468 c 0,0 95.98506,-31.99503 95.98506,-111.98257" />
                  <path d="M 223.98505,127.98211 V 31.997059 c 0,0 -31.99502,-15.997511 -95.98506,-15.997511" />
                  <path d="m 128,239.96468 c 0,0 -95.985056,-31.99503 -95.985056,-111.98257" />
                  <path d="M 32.014944,127.98211 V 31.997059 c 0,0 31.995019,-15.997509 95.985056,-15.997509" />
                  <path d="M 191.99003,63.99208 C 128,111.9846 112.00249,175.97464 112.00249,175.97464 c 0,0 -15.997511,-19.0946 -31.995019,-31.99502" />
                </svg>
              ),
              title: "Actually private",
              body: "End-to-end encrypted, so your family's stuff stays your family's stuff. We can't read it, and neither can anyone else.",
            },
            { icon: <svg fill="#000000" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 52 52" xmlSpace="preserve">
<g>
	<path d="M34.7,36.1c0.5-0.5,0.5-1.3,0-1.8l-1.8-1.8c-0.5-0.5-1.3-0.5-1.8,0l-4.4,4.4c-0.3,0.3-0.9,0.3-1.2,0
		l-4.4-4.4c-0.5-0.5-1.3-0.5-1.8,0l-1.8,1.8c-0.5,0.5-0.5,1.3,0,1.8l4.4,4.4c0.3,0.3,0.3,0.9,0,1.2l-4.4,4.4c-0.5,0.5-0.5,1.3,0,1.8
		l1.8,1.8c0.5,0.5,1.3,0.5,1.8,0l4.4-4.4c0.3-0.3,0.9-0.3,1.2,0l4.4,4.4c0.5,0.5,1.3,0.5,1.8,0l1.8-1.8c0.5-0.5,0.5-1.3,0-1.8
		l-4.4-4.4c-0.3-0.3-0.3-0.9,0-1.2L34.7,36.1z"/>
	<path d="M47.7,11.6c-5.5-6.1-13.3-9.5-21.6-9.5S10,5.5,4.5,11.6C4.1,12,4.2,12.7,4.6,13l3,2.6C8,16,8.6,15.9,9,15.5
		c4.4-4.7,10.6-7.4,17.1-7.4s12.7,2.7,17.1,7.4c0.4,0.4,1,0.4,1.4,0.1l3-2.6C48,12.6,48.1,12,47.7,11.6z"/>
	<path d="M26.1,16.1c-4.2,0-8.2,1.8-11,5c-0.4,0.4-0.3,1.1,0.1,1.5l3.2,2.4c0.4,0.3,1,0.3,1.3-0.1
		c1.7-1.8,4-2.8,6.4-2.8s4.7,1,6.3,2.7c0.3,0.4,0.9,0.4,1.3,0.1l3.2-2.4c0.5-0.4,0.5-1,0.1-1.5C34.3,17.9,30.3,16.1,26.1,16.1z"/>
</g>
</svg>, title: "Works offline", body: "Add to a list in a dead zone at the store. It syncs the second you're back online." },
          ].map(({ icon, title, body }) => (
            <div key={title}>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <span style={{ display: "inline-flex", flexShrink: 0 }}>{icon}</span>
                <p style={{ fontWeight: 600, margin: 0 }}>{title}</p>
              </div>
              <p style={{ color: "var(--color-muted)", fontSize: "14px", lineHeight: 1.5, marginTop: "4px", marginLeft: "30px" }}>{body}</p>
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </main>
  );
}
